import React, { PureComponent } from 'react';
import ReactTable from 'react-table';
import 'react-table/react-table.css';
import styled from 'styled-components';
import ComponentTitle from './ComponentTitle';
import UpdatedDate from './UpdatedDate';
import SortAscendingIcon from './assets/SortAscendingIcon.svg';
import SortDescendingIcon from './assets/SortDescendingIcon.svg';
import DownLoadButton from './DownLoadButton';

const StyledTableComponent = styled.div`
  grid-column: span 12;
  @media (min-width: 1280px) {
    grid-column: span ${props => props.columns};
  }
  @media print {
    page-break-inside: avoid;
    margin-bottom: 30px;
    display: block;
  }
`;

const TableWrapper = styled.div`
  padding: 10px 10px 40px 10px;
  border-radius: 5px;
  position: relative;
  background: white;
  box-shadow: 0 2px 4px 0 rgba(38, 47, 51, 0.3);
  overflow: hidden;
  .ReactTable {
    color: #262f33;
    border: 0;
    font-size: 13px;
    .rt-thead {
      font-family: latoblack;
      &.-header {
        box-shadow: none;
        padding-top: 10px;
        padding-bottom: 10px;
        border-bottom: 1px solid #6d7b80;
      }
      .rt-th {
        text-align: left;
        font-size: 13px;
        border-right-color: #6d7b80;
        padding: 5px 21px 5px 10px;
        background-repeat: no-repeat;
        background-position: calc(100% - 5px) center;
        &.type-number {
          text-align: right;
        }
        &.-sort-asc {
          box-shadow: none;
          background-image: url(${props => props.sortDescendingIconUrl});
        }
        &.-sort-desc {
          box-shadow: none;
          background-image: url(${props => props.sortAscendingIconUrl});
        }
      }
    }
    .rt-tbody {
      font-family: latoregular;
      .rt-tr {
        &.-even {
          background-color: #ffffff;
        }
        &.-odd {
          background-color: #f0f2f2;
        }
      }
      .rt-tr-group {
        border-bottom: 0;
      }
      .rt-td {
        text-align: left;
        border-right: 0;
        padding: 15px 21px 15px 10px;
        &.type-number {
          text-align: right;
        }
        &.type-percent {
          text-align: right;
        }
      }
    }
    .rt-tfoot {
      font-family: latoblack;
      .rt-tr {
        box-shadow: none;
        padding-top: 10px;
        padding-bottom: 10px;
        border-top: 1px solid #6d7b80;
      }
      .rt-td {
        text-align: left;
        font-size: 13px;
        border-right-color: #6d7b80;
        padding: 5px 21px 5px 10px;
        background-repeat: no-repeat;
        background-position: calc(100% - 5px) center;
        &.type-number {
          text-align: right;
        }
      }
    }
  }
`;

class TableComponent extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {};
    this.defaultSortMethod = this.defaultSortMethod.bind(this);
  }

  betterValueOf(value) {
    let betterValue = value;
    (value === null || value === undefined || value === '-') &&
      (betterValue = -Infinity);

    let valueAsNumber = parseFloat(
      !isNaN(betterValue)
        ? betterValue // If it's a number, parse it into one
        : betterValue.replace(/\s|%/g, '').replace(/,/, '.') // Otherwise, give it another change after transform
    );
    !isNaN(valueAsNumber) && (betterValue = valueAsNumber);
    return betterValue;
  }

  valueType(value) {
    let type = typeof this.betterValueOf(value);
    if (type === 'number') {
      // Percent is actually still a string
      if (typeof value === 'string' && value.includes('%')) {
        return 'percent';
      } else if (typeof value === 'string') {
        // Empty or dashes
        if (value.match('^-$') || value === '') return 'empty';
      }
    }
    return type;
  }

  determineColumnType(values) {
    let allPercent = true;
    let allNumbers = true;
    for (let value of values) {
      let type = this.valueType(value);
      if (type !== 'empty') {
        if (type !== 'number') allNumbers = false;
        if (type !== 'percent') allPercent = false;
      }
    }
    if (allPercent) {
      return 'percent';
    } else if (allNumbers) {
      return 'number';
    } else {
      return 'string';
    }
  }

  maxLength(values) {
    let maxLength = 0;
    for (let value of values) {
      if (value.length > maxLength) {
        maxLength = value.length;
      }
    }
    return maxLength;
  }

  calculateTotal(values) {
    if (this.determineColumnType(values) !== 'number') {
      return '';
    } else {
      // number can actually be a string if it contains negatives
      let sum = 0;
      for (let value of values) {
        if (this.valueType(value) !== 'empty') {
          if (typeof value === 'string') {
            sum = sum + parseFloat(value.replace(/\s/g, '').replaceAll('.', '')); // Remove . to correctly calculate sum
          } else if (typeof value === 'number') {
            sum = sum + value;
          }
        }
      }
      return sum.toLocaleString('de'); // Format with . as 1000 separator
    }
  }

  defaultSortMethod(a, b, desc) {
    // force null and undefined to the bottom
    a = a === null || a === undefined || a === '-' ? -Infinity : a;
    b = b === null || b === undefined || b === '-' ? -Infinity : b;

    // force any string values to lowercase
    a = typeof a === 'string' ? a.toLowerCase() : a;
    b = typeof b === 'string' ? b.toLowerCase() : b;

    a = this.betterValueOf(a);
    b = this.betterValueOf(b);

    // Return either 1 or -1 to indicate a sort priority
    if (a > b) {
      return 1;
    }
    if (a < b) {
      return -1;
    }
    // returning 0 or undefined will use any subsequent column sorting methods or the row index as a tiebreaker
    return 0;
  }

  colorCodePercentages(row) {
    return (
      <span
        style={{
          backgroundColor:
            row.value.length > 1 && row.value.charAt(0) === '-'
              ? '#fd91a9'
              : '#d0e385',
          transition: 'all .3s ease',
          borderRadius: '5px',
          padding: '3px 8px'
        }}
      >
        {row.value}
      </span>
    );
  }

  createCSVData(data) {
    return this.makeExcelFriendlyNumbers(data);
  }

  makeExcelFriendlyNumbers(originalarray) {
    // need to clone the array otherwise the values get also changed in the table
    var array = JSON.parse(JSON.stringify(originalarray));
    for (var i in array) {
      var obj = array[i];
      for (var prop in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, prop)) {
          var value = obj[prop];
          if (typeof value === 'string' && /[0-9]+\.[0-9]+/.test(value)) {
            obj[prop] = value.replace('.', '');
          }
          if (typeof value === 'string' && /[0-9]+․[0-9]+/.test(value)) {
            obj[prop] = value.replace('․', '');
          }
          if (typeof value === 'string' && /[0-9]+\s[0-9]+/.test(value)) {
            obj[prop] = value.replace(/\s/g, '');
          }
        }
        // To make the data appear we need to replace . with _
        // TODO This also changes the order of the property in the download.
        if(prop.toString().includes('.')){
          var prop2 = prop.toString().replaceAll('.','_');
          obj[prop2] = obj[prop];
          delete obj[prop];
        }
      }
    }
    return array;
  }

  pivot(data) {
    var iter = function(pivoted, record) {
      for (var key in record) {
        if (!pivoted[key]) pivoted[key] = [];
        pivoted[key].push(record[key]);
      }
      return pivoted;
    };
    return data.reduce(iter, {});
  }

  render() {
    let columns = [];
    const { title, info } = this.props;
    const data = JSON.parse(this.props.data);
    const magicSpacing = 20;

    let dataP = this.pivot(data);

    for (const key of Object.keys(data[0])) {
      let column = dataP[key];
      let columnType = this.determineColumnType(column);
      let collumnDefs = {
        Header: key,
        // Avoiding . in accessor (e.g. Änderung seit 1.1.2021) to screw up accessor
        // https://github.com/tannerlinsley/react-table/issues/1671
        accessor: (d) => d[key], 
        id: key,
        minWidth: 1,
        className: `type-${columnType}`,
        headerClassName: `type-${columnType}`,
        Footer: ''
      };

      if (
        columnType === 'number' &&
        !key.toLowerCase().includes('plz') &&
        !key.toLowerCase().includes('nummer') &&
        !Object.keys(data[0]).includes('Filter') // Hack to not show totals if it is a filter table
      ) {
        collumnDefs.Footer = this.calculateTotal(column) + '';
      } else if (columnType === 'number') {
        collumnDefs.maxWidth = Math.min(
          200,
          Math.max(
            key.length * magicSpacing,
            this.maxLength(column) * magicSpacing
          )
        );
      } else if (columnType === 'percent') {
        collumnDefs.Cell = this.colorCodePercentages;
      }
      columns.push(collumnDefs);
    }

    return (
      <StyledTableComponent columns={this.props.columns}>
        <ComponentTitle
          title={title}
          info={info}
          csvData={this.createCSVData(data)}
          lastUpdated={this.props.lastUpdated}
        />
        <TableWrapper
          sortAscendingIconUrl={SortAscendingIcon}
          sortDescendingIconUrl={SortDescendingIcon}
        >
          <ReactTable
            data={data}
            columns={columns}
            defaultSortMethod={this.defaultSortMethod}
            showPagination={false}
            defaultPageSize={10000}
            minRows={0}
            style={{
              maxHeight: `${
                this.props.heightInPx ? `${this.props.heightInPx}px` : '660px'
              }`
            }}
          />
          <DownLoadButton
            url={this.props.downloadurl}
            iconColor={`#a3a5a7`}
            iconWidth={`28px`}
            iconHeight={`21px`}
            csvData={this.createCSVData(data)}
            title={title + ' ' + this.props.lastUpdated}
          />
          <UpdatedDate>{`letztes Update: ${
            this.props.lastUpdated
          }`}</UpdatedDate>
        </TableWrapper>
      </StyledTableComponent>
    );
  }
}

export default TableComponent;
